/**
 * Wie hoch die Tastatur ins Bild ragt — und wie die Blätter davon erfahren.
 *
 * **Das Problem.** Im WKWebView schiebt die Tastatur das Fenster nicht zusammen, sie legt sich
 * **darüber**. Ein Blatt, das mit `bottom: 0` am unteren Rand klebt, liegt dann darunter: im
 * Querformat war von der Trefferliste der Prüflingssuche nichts mehr zu sehen, sobald der
 * erste Buchstabe getippt war (Befund des Nutzers, 2026-09-13).
 *
 * **Der Weg.** `window.visualViewport` kennt den wirklich sichtbaren Ausschnitt. Die Differenz
 * zum Fenster ist die Tastatur; sie wird als CSS-Variable `--tastatur` an die Wurzel
 * geschrieben, und `Sheet` und `Dialog` rücken um diesen Betrag hoch. Kein Plugin: die
 * Auskunft steht im Web-Teil schon bereit (Safari 13+, iOS 15 also sicher).
 */

/** Was von `visualViewport` gebraucht wird — zwei Zahlen, damit es sich prüfen lässt. */
export interface Sichtfenster {
  height: number;
  offsetTop: number;
}

/**
 * Ein Pixel Schwelle. Retina rundet, und beim Drehen weicht die Sicht für einen Augenblick um
 * Bruchteile ab — ohne Schwelle rückten die Blätter dabei sichtbar.
 */
const SCHWELLE = 1;

/**
 * Wie viele Pixel der unteren Fensterhöhe verdeckt sind.
 *
 * ⚠️ **`offsetTop` gehört dazu.** Safari kürzt den Ausschnitt nicht nur, es schiebt ihn auch
 * nach oben, sobald das Eingabefeld sonst unter der Tastatur läge. Rechnete man nur mit der
 * Höhe, käme zu wenig heraus und das Blatt stünde weiterhin halb im Verdeckten.
 */
export function ueberdeckung(fensterHoehe: number, sicht: Sichtfenster | null): number {
  if (!sicht) return 0;
  const verdeckt = fensterHoehe - (sicht.height + sicht.offsetTop);
  return verdeckt > SCHWELLE ? verdeckt : 0;
}

/**
 * Hängt die Rechnung an das Fenster und schreibt `--tastatur` an die Wurzel.
 *
 * Gibt eine Aufräumfunktion zurück. Läuft einmal beim Start (`main.tsx`) — ein Horcher für die
 * ganze App, kein React-Zustand: die Blätter lesen die Variable im CSS, und ein Zustand hier
 * zeichnete bei jedem Tastaturbild die halbe Oberfläche neu.
 */
export function beobachteTastatur(): () => void {
  const sicht = window.visualViewport;
  if (!sicht) return () => undefined;

  const setzen = (): void => {
    const hoehe = ueberdeckung(window.innerHeight, sicht);
    document.documentElement.style.setProperty('--tastatur', hoehe + 'px');
  };

  setzen();
  sicht.addEventListener('resize', setzen);
  // Auch beim Rollen: derselbe Ausschnitt wandert, wenn Safari das Feld ins Bild schiebt.
  sicht.addEventListener('scroll', setzen);
  window.addEventListener('orientationchange', setzen);

  return () => {
    sicht.removeEventListener('resize', setzen);
    sicht.removeEventListener('scroll', setzen);
    window.removeEventListener('orientationchange', setzen);
    document.documentElement.style.removeProperty('--tastatur');
  };
}
